import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined'
import CheckCircleOutlineRoundedIcon from '@mui/icons-material/CheckCircleOutlineRounded'
import EventRepeatRoundedIcon from '@mui/icons-material/EventRepeatRounded'
import NotificationsActiveRoundedIcon from '@mui/icons-material/NotificationsActiveRounded'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import PaidRoundedIcon from '@mui/icons-material/PaidRounded'
import PlayCircleFilledRoundedIcon from '@mui/icons-material/PlayCircleFilledRounded'
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined'
import SwapHorizRoundedIcon from '@mui/icons-material/SwapHorizRounded'
import WhatsAppIcon from '@mui/icons-material/WhatsApp'
import {
  Box,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import { useState } from 'react'
import { toast } from 'react-toastify'
import { displayNamesOfEventTypes } from 'src/consumer/constants'
import {
  markSupportBookedEventCompleted,
  sendSupportBookedEventJoinLink,
} from 'src/supportPortal/services/supportBookedEventActionsService'
import { isSupportSessionStaleInProgress } from 'src/mentor/components/MentorEventsPage/components/SupportBookingCardParts'
import { getSessionMediaCapabilities } from './SupportBookingCardActionMenus'

dayjs.extend(utc)

function ActionRow({ icon, iconColor, label, description, onClick, disabled, danger }) {
  return (
    <Stack
      component="button"
      type="button"
      direction="row"
      spacing={1.25}
      alignItems="center"
      disabled={disabled}
      onClick={onClick}
      sx={{
        width: '100%',
        textAlign: 'left',
        appearance: 'none',
        border: '1px solid rgba(226, 232, 240, 0.9)',
        borderRadius: '10px',
        backgroundColor: '#ffffff',
        px: 1.25,
        py: 1,
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.55 : 1,
        fontFamily: 'inherit',
        transition: 'background-color 0.12s ease, border-color 0.12s ease',
        '&:hover': disabled
          ? {}
          : {
              backgroundColor: danger ? 'rgba(254, 242, 242, 0.9)' : '#f8fafc',
              borderColor: danger ? 'rgba(220, 38, 38, 0.4)' : 'rgba(148, 163, 184, 0.6)',
            },
      }}
    >
      <Box
        sx={{
          width: 30,
          height: 30,
          borderRadius: '8px',
          display: 'grid',
          placeItems: 'center',
          backgroundColor: `${iconColor}14`,
          color: iconColor,
          flexShrink: 0,
        }}
      >
        {icon}
      </Box>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: danger ? '#b91c1c' : '#1e293b' }}>
          {label}
        </Typography>
        {description ? (
          <Typography sx={{ fontSize: 11.5, color: '#64748b', lineHeight: 1.3 }}>
            {description}
          </Typography>
        ) : null}
      </Box>
    </Stack>
  )
}

/**
 * Status/session-aware quick-actions dialog opened by clicking a booking's time slot. Surfaces
 * exactly the actions that make sense for the booking's current state; the heavier flows are
 * delegated back to the page's drawers/dialogs via callbacks, while mark-completed and
 * send-reminder run in place.
 */
export default function SupportQuickActionsDialog({
  open,
  onClose,
  bookedEvent,
  hasVcAuditAccess = false,
  hasRecordingPreferenceAccess = false,
  canManageActions = false,
  canMarkPaid = false,
  onDetails,
  onReschedule,
  onReassign,
  onMarkPaid,
  onFeeDetails,
  onCancel,
  onWatchLive,
  onRemindComplete,
  onActionSuccess,
}) {
  const [busyAction, setBusyAction] = useState('')

  if (!bookedEvent) {
    return null
  }

  const isCancelled = bookedEvent?.status === 'cancelled'
  const isCompleted =
    bookedEvent?.markedCompleted ||
    bookedEvent?.sessionStatus === 'marked-completed' ||
    bookedEvent?.sessionStatus === 'finished'
  const isInProgress = bookedEvent?.sessionStatus === 'in-progress'
  const isStaleInProgress = isSupportSessionStaleInProgress(bookedEvent)
  const isPaymentPending =
    bookedEvent?.status === 'booking-initiated' && bookedEvent?.paymentStatus === 'pending'
  const isOnline = bookedEvent?.meetingSrc === 'aumhum'
  const isBooked = bookedEvent?.status === 'booked'
  const mediaCapabilities = getSessionMediaCapabilities(
    bookedEvent,
    hasVcAuditAccess,
    hasRecordingPreferenceAccess,
  )

  const patientName = bookedEvent?.patientInfoData?.name || bookedEvent?.userName || 'Patient'
  const eventTypeLabel =
    displayNamesOfEventTypes?.[bookedEvent?.eventType] || bookedEvent?.eventType || 'Session'
  const timeLabel = bookedEvent?.startTime
    ? `${dayjs.utc(bookedEvent.startTime).local().format('ddd, DD MMM · h:mma')}${
        bookedEvent?.endTime ? `–${dayjs.utc(bookedEvent.endTime).local().format('h:mma')}` : ''
      }`
    : 'Time not set'
  const statusLabel = isCancelled
    ? 'Cancelled'
    : isPaymentPending
      ? 'Payment pending'
      : isCompleted
        ? 'Completed'
        : isStaleInProgress
          ? 'Started, not completed'
          : isInProgress
            ? 'In progress'
            : isBooked
              ? 'Booked'
              : 'Initiated'

  function close() {
    setBusyAction('')
    onClose?.()
  }

  function delegate(handler) {
    close()
    handler?.(bookedEvent)
  }

  async function handleMarkCompleted() {
    setBusyAction('complete')
    const result = await markSupportBookedEventCompleted(bookedEvent?.id)
    setBusyAction('')
    if (result.ok) {
      toast.success('Session marked completed')
      close()
      onActionSuccess?.()
    } else {
      toast.error(result.message)
    }
  }

  async function handleSendReminder() {
    setBusyAction('reminder')
    const result = await sendSupportBookedEventJoinLink({
      bookedEventId: bookedEvent?.id,
      kind: 'secure',
    })
    setBusyAction('')
    if (result.ok) {
      toast.success("Joining link sent to the patient's WhatsApp")
      close()
      onActionSuccess?.()
    } else {
      toast.error(result.message)
    }
  }

  const rows = []
  if (isStaleInProgress && onRemindComplete) {
    rows.push(
      <ActionRow
        key="remind-complete"
        icon={<NotificationsActiveRoundedIcon sx={{ fontSize: 18 }} />}
        iconColor="#d97706"
        label="Remind practitioner to complete"
        description="Started but never marked complete — nudge them via WhatsApp/email"
        onClick={() => delegate(onRemindComplete)}
      />,
    )
  }
  if (mediaCapabilities.canPlayLive && onWatchLive) {
    rows.push(
      <ActionRow
        key="watch-live"
        icon={<PlayCircleFilledRoundedIcon sx={{ fontSize: 18 }} />}
        iconColor="#ef4444"
        label={bookedEvent?.vcAuditLiveBadgeLabel || 'Watch Live'}
        description="Audit this live session in real time"
        onClick={() => delegate(onWatchLive)}
      />,
    )
  }
  if (isOnline && !isCancelled) {
    rows.push(
      <ActionRow
        key="reminder"
        icon={<WhatsAppIcon sx={{ fontSize: 18 }} />}
        iconColor="#128c7e"
        label="Send reminder"
        description="Send the secure joining link to the patient on WhatsApp"
        disabled={busyAction === 'reminder'}
        onClick={handleSendReminder}
      />,
    )
  }
  if (!isCancelled && !isCompleted) {
    rows.push(
      <ActionRow
        key="reschedule"
        icon={<EventRepeatRoundedIcon sx={{ fontSize: 18 }} />}
        iconColor="#4f46e5"
        label="Reschedule"
        description="Move this booking to a new slot"
        onClick={() => delegate(onReschedule)}
      />,
    )
  }
  if (canManageActions && !isCancelled && !isCompleted && onReassign) {
    rows.push(
      <ActionRow
        key="reassign"
        icon={<SwapHorizRoundedIcon sx={{ fontSize: 18 }} />}
        iconColor="#0e7490"
        label="Reassign practitioner"
        onClick={() => delegate(onReassign)}
      />,
    )
  }
  if (canMarkPaid && isPaymentPending && onMarkPaid) {
    rows.push(
      <ActionRow
        key="mark-paid"
        icon={<PaidRoundedIcon sx={{ fontSize: 18 }} />}
        iconColor="#047857"
        label="Mark as paid"
        description="Confirm an out-of-band payment"
        onClick={() => delegate(onMarkPaid)}
      />,
    )
  }
  if (canManageActions && isBooked && !isCompleted && !isCancelled) {
    rows.push(
      <ActionRow
        key="mark-completed"
        icon={<CheckCircleOutlineRoundedIcon sx={{ fontSize: 18 }} />}
        iconColor="#047857"
        label="Mark completed"
        description="Finish the session on behalf of the practitioner"
        disabled={busyAction === 'complete'}
        onClick={handleMarkCompleted}
      />,
    )
  }
  rows.push(
    <ActionRow
      key="fee-details"
      icon={<ReceiptLongOutlinedIcon sx={{ fontSize: 18 }} />}
      iconColor="#475569"
      label="Fee details"
      onClick={() => delegate(onFeeDetails)}
    />,
  )
  rows.push(
    <ActionRow
      key="details"
      icon={<InfoOutlinedIcon sx={{ fontSize: 18 }} />}
      iconColor="#475569"
      label="Full details"
      description="Schedule, payment, links, history and support activity"
      onClick={() => delegate(onDetails)}
    />,
  )
  if (!isCancelled && !isCompleted && onCancel) {
    rows.push(
      <ActionRow
        key="cancel"
        icon={<CancelOutlinedIcon sx={{ fontSize: 18 }} />}
        iconColor="#b91c1c"
        label="Cancel booking"
        description="Cancel and choose a refund recommendation"
        danger
        onClick={() => delegate(onCancel)}
      />,
    )
  }

  return (
    <Dialog
      open={open}
      onClose={close}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { borderRadius: '14px' } }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Stack direction="row" spacing={1} alignItems="flex-start" justifyContent="space-between">
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontWeight: 800, fontSize: 16.5, color: '#0f172a' }}>
              {patientName}
            </Typography>
            <Typography sx={{ fontSize: 12.5, color: '#64748b' }}>
              {eventTypeLabel} · {timeLabel}
            </Typography>
          </Box>
          <Chip label={statusLabel} size="small" sx={{ fontWeight: 700, flexShrink: 0 }} />
        </Stack>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={0.85} sx={{ mt: 0.5, pb: 1 }}>
          {rows}
        </Stack>
      </DialogContent>
    </Dialog>
  )
}
